import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import { PermissionService } from './permission.service';

interface User {
  email: string;
  role: string;
  token: string;
  data?: User;
}

export interface RegistrationOtpResponse {
  status?: boolean;
  message?: string;
  data?: {
    externalUser?: string;
  };
}

@Injectable()
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);


  private currentUserSubject = new BehaviorSubject<User | null>(
    (() => {
      const storedUser = localStorage.getItem('user');
      return storedUser ? (JSON.parse(storedUser) as User) : null;
    })()
  );

  currentUser$ = this.currentUserSubject.asObservable();
  private baseUrl = environment.baseUrl;
  private publicUrl = environment.baseUrl + 'public/';

  login(email: string, password: string) {
    return this.http.post<{ status: boolean; message: string }>(
      `${this.baseUrl}public/auth/login`,
      { email, password }
    );
  }


  verifyOtp(email: string, otp: string) {
    return this.http.post<any>(`${this.baseUrl}public/auth/verify-otp`, { email, otp }).pipe(
      tap(res => {
        localStorage.setItem('user', JSON.stringify(res.data));
        localStorage.setItem('token', res.data.token);
        this.currentUserSubject.next(res.data);
        this.permissionService.refreshFromLocalStorage();
      })
    );
  }

  logout() {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('businessRegistrationDraft');
    localStorage.clear();
    this.currentUserSubject.next(null);
    this.router.navigate(['/']).then(() => {
      window.location.reload();
    });
  }

  resendOtp(email: string) {
    return this.http.post(`${this.baseUrl}public/auth/resend-otp`, { email });
  }

  sendRegistrationOtp(email: string): Observable<any> {
    return this.http.post(`${this.publicUrl}auth/registration/send-otp`, { email });
  }

  verifyRegistrationOtp(email: string, otp: string): Observable<RegistrationOtpResponse> {
    return this.http.post<RegistrationOtpResponse>(`${this.publicUrl}auth/registration/verify-otp`, { email, otp });
  }

  activatePassword(token: string, newPassword: string, confirmPassword: string): Observable<any> {
    const url = this.baseUrl + 'public/auth/reset-password';
    const payload = {
      token,
      newPassword,
      confirmPassword,
    };
    return this.http.post(url, payload);
  }

  resetPassword(
    token: string,
    newPassword: string,
    confirmPassword: string
  ): Observable<any> {
    const url = this.baseUrl + 'public/auth/reset-password';
    const payload = {
      token,
      newPassword,
      confirmPassword,
    };
    return this.http.post(url, payload);
  }

  forgotPassword(email: string): Observable<any> {
    const url = this.baseUrl + 'public/auth/forgot-password';
    const body = {
      email,
    };
    
    return this.http.post(url, body);
  }

  changePassword(
    userId: string,
    currentPassword: string,
    newPassword: string
  ): Observable<any> {
    const url = this.baseUrl + 'users/change-password';
    const body = { userId, currentPassword, newPassword };
    return this.http.post(url, body);
  }

  get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }
}
